<template>
  <div class="flex flex-col h-full w-[550px] gap-5 mx-10 mt-5">
    <InputGroup class="w-full!">
      <Select
        v-model="selectedReminder"
        :options="reminders"
        option-label="reminderTitle"
        class="w-full rounded-r-none"
        scrollHeight="30rem"
        @change="reminderSelected"
      ></Select>

      <Button v-tooltip.top="'Create'" icon="pi pi-plus-circle" outlined class="p-2!" @click="addReminder"></Button>
      <Button v-tooltip.top="'Edit'" icon="pi pi-pencil" outlined class="p-2!" :disabled="selectedReminder == null" @click="editReminder"></Button>
    </InputGroup>

    <Textarea v-model="reminderText" class="resize-none" rows="5" :disabled="true"></Textarea>
    <div class="flex flex-row justify-evenly">
      <LabeledCheckboxNoForm v-model="enabled" name="enabled" label="Enabled" label-location="left" size="" class="my-auto"></LabeledCheckboxNoForm>
      <LabeledCheckboxNoForm
        v-model="privateReminder"
        name="privateReminder"
        label="Private"
        label-location="left"
        size=""
        class="my-auto"
      ></LabeledCheckboxNoForm>
      <div class="flex flex-row gap-3">
        <p class="font-bold my-auto">Completed Date</p>
        <DatePicker
          manualInput
          showIcon
          show-button-bar
          icon-display="button"
          inputId="modelField"
          class="h-9"
          input-class="w-36"
          panelClass="bg-white dark:bg-surface-700/80 backdrop-blur-xl"
          v-model="completedDate"
          dateFormat="m/d/yy"
        />
      </div>
    </div>
    <Card class="border-surface-300 dark:border-surface-600 shadow-none" pt:body:class="p-0!" pt:content:class="p-0!">
      <template #content>
        <div class="flex flex-row justify-center gap-2 py-2 bg-surface-100 dark:bg-surface-800 rounded-t-md">
          <p class="font-bold my-auto">Upon This Event</p>
        </div>
        <div class="flex flex-row p-5">
          <RadioButtonGroup
            v-model="selectedEvent"
            name="event"
            :options="events"
            option-label="name"
            button-class="mt-[0.6rem]"
            direction="vertical"
            class="w-32"
            @change="eventSelected"
          ></RadioButtonGroup>
          <div class="flex flex-col gap-3 w-auto">
            <div class="flex flex-row gap-2">
              <InputNumber
                v-model="drawNumber"
                class="h-10"
                :input-class="'w-14 text-center ' + getBackgroundColor(1)"
                :min="0"
                :max-fraction-digits="0"
                :disabled="selectedEvent == null || selectedEvent.key !== 1"
              ></InputNumber>
            </div>
            <div class="flex flex-row gap-2">
              <InputNumber
                v-model="percentComplete"
                class="h-10"
                :input-class="'w-14 text-center ' + getBackgroundColor(2)"
                :min="0"
                :max-fraction-digits="0"
                :disabled="selectedEvent == null || selectedEvent.key !== 2"
              ></InputNumber>
              <p class="my-auto font-bold">% Complete</p>
            </div>
            <div class="flex flex-row gap-2">
              <InputNumber
                v-model="percentDrawn"
                class="h-10"
                :input-class="'w-14 text-center ' + getBackgroundColor(3)"
                :min="0"
                :max-fraction-digits="0"
                :disabled="selectedEvent == null || selectedEvent.key !== 3"
              ></InputNumber>
              <p class="my-auto font-bold">% Drawn</p>
            </div>
            <div class="flex flex-row gap-2">
              <InputText
                v-model="lineNumber1"
                :class="getBackgroundColor(4)"
                class="h-10 w-28"
                :disabled="selectedEvent == null || selectedEvent.key !== 4"
              ></InputText>
              <p class="my-auto font-bold">is</p>
              <InputNumber
                v-model="linePercentComplete"
                class="h-10"
                :input-class="'w-14 text-center ' + getBackgroundColor(4)"
                :min="0"
                :max-fraction-digits="0"
                :disabled="selectedEvent == null || selectedEvent.key !== 4"
              ></InputNumber>
              <p class="my-auto font-bold">% Complete</p>
            </div>
            <div class="flex flex-row gap-2">
              <InputText
                v-model="lineNumber2"
                :class="getBackgroundColor(5)"
                class="h-10 w-28"
                :disabled="selectedEvent == null || selectedEvent.key !== 5"
              ></InputText>
              <p class="my-auto font-bold">Complete % > Site Complete %</p>
            </div>
            <div class="flex flex-row gap-2">
              <DatePicker
                manualInput
                showIcon
                show-button-bar
                icon-display="button"
                inputId="modelField"
                class="h-9"
                :input-class="'w-36 ' + getBackgroundColor(6)"
                panelClass="bg-white dark:bg-surface-700/80 backdrop-blur-xl"
                v-model="reminderDate"
                dateFormat="m/d/yy"
                :disabled="selectedEvent == null || selectedEvent.key !== 6"
              />
            </div>
          </div>
        </div>
      </template>
    </Card>
    <SubmitButton :show-icon="false" @click="submitProjectReminder"></SubmitButton>
  </div>

  <TSDialog v-model:visible="mode" size="w-auto" :label="mode + ' Reminder'" :icon="mode === 'Add' ? 'plus-circle' : 'pencil'">
    <template #content>
      <MaintainReminders
        :reminders="reminders"
        :editing-reminder="mode === 'Edit' ? selectedReminder : undefined"
        @added="addedReminder"
        @edited="editedReminder"
      ></MaintainReminders>
    </template>
  </TSDialog>
</template>

<script setup lang="ts">
import { ProjectReminderSchema, type ProjectReminder, type Reminder } from "@/types/Reminder";

const reminders = defineModel<Reminder[]>("reminders", { required: true });

const emit = defineEmits<{
  edited: [editedProjectReminder: ProjectReminder];
  added: [newProjectReminder: ProjectReminder];
  reminderEdited: [reminder: Reminder];
}>();

interface Props {
  projectId: string;
  lastOrdinal: number;
  editingProjectReminder?: ProjectReminder;
}
const props = defineProps<Props>();

const mode = ref<"Add" | "Edit">();

const selectedReminder = ref<Reminder>();
const reminderText = ref("");
const enabled = ref(true);
const privateReminder = ref(true);

const drawNumber = ref();
const percentComplete = ref();
const percentDrawn = ref();
const lineNumber1 = ref("");
const linePercentComplete = ref();
const lineNumber2 = ref("");
const reminderDate = ref<Date | null>(null);
const completedDate = ref<Date | null>(null);

const events = ref([
  { name: "Draw", key: 1 },
  { name: "Site is", key: 2 },
  { name: "Budget is", key: 3 },
  { name: "Line #", key: 4 },
  { name: "Line #", key: 5 },
  { name: "Date", key: 6 },
]);
const selectedEvent = ref();

const getBackgroundColor = (row: number): string => {
  if (selectedEvent.value != null && selectedEvent.value.key === row) return "bg-secondary-500/30!";
  return "bg-surface-200/30!";
};

const reminderSelected = (): void => {
  reminderText.value = selectedReminder.value != null ? selectedReminder.value.reminderText : "";
};

const addReminder = (): void => {
  mode.value = "Add";
};

const editReminder = (): void => {
  mode.value = "Edit";
};

const addedReminder = (reminder: Reminder): void => {
  reminders.value.push(reminder);
  selectedReminder.value = reminders.value[reminders.value.length - 1];
  reminderSelected();
  mode.value = undefined;
};

const editedReminder = (reminder: Reminder): void => {
  for (let i = 0; i < reminders.value.length; i++) {
    const r = reminders.value[i];
    if (r.reminderId == reminder.reminderId) {
      reminders.value[i] = reminder;
      selectedReminder.value = reminders.value[i];
      reminderSelected();
      emit("reminderEdited", reminder);
      break;
    }
  }
  mode.value = undefined;
};

const eventSelected = (): void => {
  if (selectedEvent.value == null) return;

  drawNumber.value = undefined;
  percentComplete.value = undefined;
  percentDrawn.value = undefined;
  lineNumber1.value = "";
  linePercentComplete.value = undefined;
  lineNumber2.value = "";
  reminderDate.value = null;

  switch (selectedEvent.value.key) {
    case 1:
      drawNumber.value = 0;
      break;
    case 2:
      percentComplete.value = 0;
      break;
    case 3:
      percentDrawn.value = 0;
      break;
    case 4:
      lineNumber1.value = "";
      linePercentComplete.value = 0;
      break;
    case 5:
      lineNumber2.value = "";
      break;
    case 6:
      reminderDate.value = new Date();
      break;
  }
};

const submitProjectReminder = (): void => {
  if (selectedReminder.value == null) {
    error("Invalid Project Reminder", "A title must be selected");
    return;
  }
  if (selectedEvent.value == null) {
    error("Invalid Project Reminder", "An event must be selected");
    return;
  }

  if (selectedEvent.value.key === 1 && drawNumber.value == null) {
    error("Invalid Project Reminder", "A draw number must be specified");
    return;
  } else if (selectedEvent.value.key === 2 && percentComplete.value == null) {
    error("Invalid Project Reminder", "A percent complete must be specified");
    return;
  } else if (selectedEvent.value.key === 3 && percentDrawn.value == null) {
    error("Invalid Project Reminder", "A percent drawn must be specified");
    return;
  } else if (selectedEvent.value.key === 4) {
    if (lineNumber1.value == null || lineNumber1.value.trim().length === 0) {
      error("Invalid Project Reminder", "A line number must be specified");
      return;
    } else if (linePercentComplete.value == null) {
      error("Invalid Project Reminder", "A percent complete must be specified");
      return;
    }
  } else if (selectedEvent.value.key === 5 && lineNumber2.value.trim().length === 0) {
    error("Invalid Project Reminder", "A line number must be specified");
    return;
  } else if (selectedEvent.value.key === 6 && reminderDate.value == null) {
    error("Invalid Project Reminder", "A date must be specified");
    return;
  }

  const projectReminder: ProjectReminder = {
    projectReminderId: props.editingProjectReminder != null ? props.editingProjectReminder.projectReminderId : "",
    projectId: props.projectId,
    ordinal: props.editingProjectReminder != null ? props.editingProjectReminder.ordinal : props.lastOrdinal + 1,
    enabled: enabled.value,
    drawNumber: drawNumber.value == null ? -1 : drawNumber.value,
    percentCompleteLineItem: linePercentComplete.value == null ? -1 : linePercentComplete.value,
    lineItem: lineNumber1.value != null ? lineNumber1.value : lineNumber2.value != null ? lineNumber2.value : "",
    reminderDate: reminderDate.value,
    percentCompleteSite: percentComplete.value == null ? -1 : percentComplete.value,
    percentDrawnBudget: percentDrawn.value == null ? -1 : percentDrawn.value,
    privateReminder: privateReminder.value,
    percentSite: lineNumber2.value != null && lineNumber2.value.length > 0 ? 1 : -1,
    completedDate: completedDate.value,
    reminder: {
      reminderId: selectedReminder.value.reminderId,
      reminderTitle: selectedReminder.value.reminderTitle,
      reminderText: selectedReminder.value.reminderText,
    },
    passed: false,
  };

  if (props.editingProjectReminder != null) {
    axios
      .post("/project-reminder/update", projectReminder)
      .then((response) => {
        handleApiResponse(
          response,
          () => {
            emit("edited", projectReminder);
          },
          (errors: ApiError[]) => {
            error("Error Occurred", errors[0].message);
          }
        );
      })
      .catch((e) => errorApi(e));
  } else {
    axios
      .post("/project-reminder/add", projectReminder)
      .then((response) => {
        handleApiResponse(
          response,
          (result) => {
            emit("added", ProjectReminderSchema.parse(result));
          },
          (errors: ApiError[]) => {
            error("Error Occurred", errors[0].message);
          }
        );
      })
      .catch((e) => errorApi(e));
  }
};

onBeforeMount(async (): Promise<void> => {
  if (props.editingProjectReminder == null) return;

  selectedReminder.value = reminders.value.find((reminder: Reminder) => reminder.reminderId === props.editingProjectReminder?.reminder.reminderId);
  selectedReminder.value = props.editingProjectReminder.reminder;
  reminderText.value = props.editingProjectReminder.reminder.reminderText;

  enabled.value = props.editingProjectReminder.enabled;
  privateReminder.value = props.editingProjectReminder.privateReminder;
  completedDate.value = props.editingProjectReminder.completedDate;

  if (props.editingProjectReminder.drawNumber !== -1) {
    selectedEvent.value = events.value[0];
    drawNumber.value = props.editingProjectReminder.drawNumber;
  } else if (props.editingProjectReminder.percentCompleteSite !== -1) {
    selectedEvent.value = events.value[1];
    percentComplete.value = props.editingProjectReminder.percentCompleteSite;
  } else if (props.editingProjectReminder.percentDrawnBudget !== -1) {
    selectedEvent.value = events.value[2];
    percentDrawn.value = props.editingProjectReminder.percentDrawnBudget;
  } else if (props.editingProjectReminder.lineItem.length > 0) {
    if (props.editingProjectReminder.percentCompleteLineItem !== -1) {
      selectedEvent.value = events.value[3];
      lineNumber1.value = props.editingProjectReminder.lineItem;
      linePercentComplete.value = props.editingProjectReminder.percentCompleteLineItem;
    } else {
      selectedEvent.value = events.value[4];
      lineNumber2.value = props.editingProjectReminder.lineItem;
    }
  } else {
    selectedEvent.value = events.value[5];
    reminderDate.value = props.editingProjectReminder.reminderDate;
  }
});
</script>
